import { scientificNotationWithPlaceholder } from 'zycommon-web/utils/symbolic-tools/common-number';

/**
 * Get shared keyboard layout based on the layout name and common keys.
 * @param {Object} commonKeys The common keys used across different virtual keyboard layouts.
 * @param {string} virtualKeyboardLayoutName The specific virtual keyboard layout name.
 * @returns {Array|Object|null} The keyboard layout, or null if no matching layout found.
 */
export function getSharedKeyboardLayout(commonKeys, virtualKeyboardLayoutName) {
    const { deleteKeyCap, exponent, fraction, moveLeftKeyCap, moveRightKeyCap, nextStepKeyCap, separator, negate } = commonKeys;
    const halfWidthSeparator = { label: '[separator]', width: 0.5 };

    // Common math keys
    const absoluteSmallFraction = { class: 'small', latex: '\\left|\\frac{#@}{#?}\\right|' };
    const centerDot = { latex: '\\cdot', aside: 'center dot' };
    const degreeKeycap = { latex: '^\\circ', aside: 'degree' };
    const expKey = { latex: '\\exp', insert: '\\exp\\left(#@\\right)' };
    const logKey = { latex: '\\log', insert: '\\log #0' };
    const naturalLogKey = { latex: '\\ln', insert: '\\ln #0' };
    const smallFraction = { class: 'small', latex: '\\frac{#@}{#0}' };
    const squareRootKeycapPlain = { latex: '\\sqrt', insert: '\\sqrt{#0}' };
    const subscriptAndSuperscript = { latex: '#@^{#?}_{#?}' };
    const subscriptPlaceholder = { latex: '#@_{#?}' };
    const superscriptPlaceholder = { latex: '#@^{#?}' };

    // Control keys
    const backspaceWidthOne = { label: '[backspace]', width: 1 };

    // Surrounds the selected text with \mathrm{}
    const textToggleKey = { class: 'action', latex: 'A\\rightarrow\\mathbf{A}', insert: '\\mathrm{#@}', aside: 'Toggle text' };

    // Trigonometric keys
    const sinKey = { latex: '\\sin', insert: '\\sin #0' };
    const cosKey = { latex: '\\cos', insert: '\\cos #0' };
    const tanKey = { latex: '\\tan', insert: '\\tan #0' };
    const sinWithShiftInverse = { latex: '\\sin', insert: '\\sin #0', shift: { latex: '\\sin^{-1}', insert: '\\sin^{-1}#0' } };
    const cosWithShiftInverse = { latex: '\\cos', insert: '\\cos #0', shift: { latex: '\\cos^{-1}', insert: '\\cos^{-1}#0' } };
    const tanWithShiftInverse = { latex: '\\tan', insert: '\\tan #0 ', shift: { latex: '\\tan^{-1}', insert: '\\tan^{-1}#0' } };

    // Calculus keys
    const definiteIntegral = { class: 'small', latex: '\\int_{#?}^{#?}', insert: '\\int_{#?}^{#?} {#0}' };
    const indefiniteIntegral = { class: 'small', latex: '\\int', insert: '\\int{#0}' };
    const summation = { class: 'small', latex: '\\sum_{#0}^{#?}', insert: '\\sum_{#0}^{#?} {#?}' };
    const product = { class: 'small', latex: '\\prod', insert: '\\prod{#0}' };

    // Accent and operator keys with smaller placeholders on keyboard display
    // Potential alternative: Use Unicode white square (□) which renders as a white outlined box to avoid shrinking the operators
    const overlineKey = { latex: '\\overline{\\scriptstyle{#0}}', insert: '\\overline{#0}' };
    const barKey = { latex: '\\bar{\\scriptstyle{#0}}', insert: '\\bar{#0}' };
    const dotKey = { latex: '\\dot{\\scriptstyle{#0}}', insert: '\\dot{#0}' };

    if (virtualKeyboardLayoutName === 'Algebra Ch6,7,8,10') {
        return {
            rows: [
                ['7', '8', '9', separator, '+', '-', '\\times', fraction],
                ['4', '5', '6', separator, '(', ')', exponent, '='],
                ['1', '2', '3', separator, 'x', 'y', separator, separator],
                [negate, '0', '.', separator, moveLeftKeyCap, moveRightKeyCap, deleteKeyCap, nextStepKeyCap],
            ],
        };
    } else if (virtualKeyboardLayoutName === 'Algebra with logs') {
        return {
            rows: [
                ['7', '8', '9', separator, '+', '-', '\\times', fraction],
                ['4', '5', '6', separator, '(', ')', exponent, '='],
                [
                    '1',
                    '2',
                    '3',
                    separator,
                    'x',
                    'y',
                    { latex: '\\log #0', insert: '\\log #0' },
                    { latex: '\\log_{#0} #0', insert: '\\log_{#0} #0' },
                ],
                [negate, '0', '.', separator, moveLeftKeyCap, moveRightKeyCap, deleteKeyCap, nextStepKeyCap],
            ],
        };
    } else if (virtualKeyboardLayoutName === 'Callister') {
        const CallisterNumericPanel = {
            label: '123',
            rows: [
                [
                    '(',
                    ')',
                    halfWidthSeparator,
                    '7',
                    '8',
                    '9',
                    '/',
                    halfWidthSeparator,
                    smallFraction,
                    '\\left|#@\\right|',
                    '\\sqrt{#@}',
                    separator,
                    separator,
                ],
                [
                    '<',
                    '>',
                    halfWidthSeparator,
                    '4',
                    '5',
                    '6',
                    '*',
                    halfWidthSeparator,
                    subscriptPlaceholder,
                    superscriptPlaceholder,
                    subscriptAndSuperscript,
                    separator,
                    separator,
                ],
                [
                    '\\approx',
                    centerDot,
                    halfWidthSeparator,
                    '1',
                    '2',
                    '3',
                    '-',
                    halfWidthSeparator,
                    { latex: '\\overline{\\scriptstyle{#0}}', insert: '\\overline{#0}', aside: 'overline' },
                    { latex: '\\bar{\\scriptstyle{#0}}', insert: '\\bar{#0}', aside: 'bar' },
                    scientificNotationWithPlaceholder,
                    separator,
                    separator,
                ],
                [
                    '\\ne',
                    '=',
                    halfWidthSeparator,
                    '0',
                    '.',
                    ',',
                    '+',
                    halfWidthSeparator,
                    textToggleKey,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };
        const CallisterGreekPanel = {
            label: '<i>&alpha;&beta;&eta;</i>',
            rows: [
                [
                    { class: 'hide-shift', latex: '\\varepsilon', aside: 'epsilon', shift: '\\char"0190' },
                    { class: 'hide-shift', latex: '\\rho', aside: 'rho', shift: '\\char"03A1' },
                    { class: 'hide-shift', latex: '\\tau', aside: 'tau', shift: '\\char"03A4' },
                    { class: 'hide-shift', latex: '\\upsilon', aside: 'upsilon', shift: '\\Upsilon' },
                    { class: 'hide-shift', latex: '\\theta', aside: 'theta', shift: '\\Theta' },
                    { class: 'hide-shift', latex: '\\iota', aside: 'iota', shift: '\\char"0399' },
                    { class: 'hide-shift', latex: '\\omicron', aside: 'omicron', shift: '\\char"039F' },
                    { class: 'hide-shift', latex: '\\pi', aside: 'pi', shift: '\\Pi' },
                ],
                [
                    { class: 'hide-shift', latex: '\\alpha', aside: 'alpha', shift: '\\char"0391' },
                    { class: 'hide-shift', latex: '\\sigma', aside: 'sigma', shift: '\\Sigma' },
                    { class: 'hide-shift', latex: '\\delta', aside: 'delta', shift: '\\Delta' },
                    { class: 'hide-shift', latex: '\\phi', aside: 'phi', shift: '\\Phi' },
                    { class: 'hide-shift', latex: '\\gamma', aside: 'gamma', shift: '\\Gamma' },
                    { class: 'hide-shift', latex: '\\eta', aside: 'eta', shift: '\\char"0397' },
                    { class: 'hide-shift', latex: '\\xi', aside: 'xi', shift: '\\Xi' },
                    { class: 'hide-shift', latex: '\\kappa', aside: 'kappa', shift: '\\Kappa' },
                    { class: 'hide-shift', latex: '\\lambda', aside: 'lambda', shift: '\\Lambda' },
                ],
                [
                    '[shift]',
                    { class: 'hide-shift', latex: '\\zeta', aside: 'zeta', shift: '\\char"0396' },
                    { class: 'hide-shift', latex: '\\chi', aside: 'chi', shift: '\\char"03A7' },
                    { class: 'hide-shift', latex: '\\psi', aside: 'psi', shift: '\\Psi' },
                    { class: 'hide-shift', latex: '\\omega', aside: 'omega', shift: '\\Omega' },
                    { class: 'hide-shift', latex: '\\beta', aside: 'beta', shift: '\\char"0392' },
                    { class: 'hide-shift', latex: '\\nu', aside: 'nu', shift: '\\char"039D' },
                    { class: 'hide-shift', latex: '\\mu', aside: 'mu', shift: '\\char"039C' },
                    '[backspace]',
                ],
                [separator, separator, separator, separator, separator, separator, separator, '[left]', '[right]', nextStepKeyCap],
            ],
        };
        const CallisterMiscPanel = {
            label: 'Misc',
            rows: [
                [
                    '#@^\\prime',
                    '#@^\\doubleprime',
                    dotKey,
                    degreeKeycap,
                    separator,
                    sinWithShiftInverse,
                    cosWithShiftInverse,
                    tanWithShiftInverse,
                    separator,
                ],
                ['*', '\\$', '\\%', '\\sqrt[{#?}]{{#0}}', separator, '\\mathscr{E}', '\\mathscr{F}', '\\operatorname{erf}', separator],
                [negate, '\\infty', '\\partial', definiteIntegral, separator, 'e^{#0}', expKey, naturalLogKey, logKey],
                ['[shift]', halfWidthSeparator, separator, separator, separator, '[left]', '[right]', backspaceWidthOne, nextStepKeyCap],
            ],
        };

        return [CallisterNumericPanel, CallisterGreekPanel, CallisterMiscPanel];
    } else if (virtualKeyboardLayoutName === 'Gerhart') {
        const GerhartNumericPanel = {
            label: '123',
            rows: [
                [
                    separator,
                    separator,
                    halfWidthSeparator,
                    separator,
                    separator,
                    separator,
                    separator,
                    halfWidthSeparator,
                    absoluteSmallFraction,
                    barKey,
                    dotKey,
                    separator,
                ],
                ['<', '>', halfWidthSeparator, '7', '8', '9', '/', halfWidthSeparator, smallFraction, barKey, '#@^\\prime', separator],
                [
                    '(',
                    ')',
                    halfWidthSeparator,
                    '4',
                    '5',
                    '6',
                    '*',
                    halfWidthSeparator,
                    subscriptPlaceholder,
                    superscriptPlaceholder,
                    scientificNotationWithPlaceholder,
                    separator,
                ],
                ['\\approx', centerDot, halfWidthSeparator, '1', '2', '3', '-', halfWidthSeparator, sinKey, cosKey, tanKey, separator],
                [
                    '\\ne',
                    '=',
                    halfWidthSeparator,
                    '0',
                    '.',
                    ',',
                    '+',
                    halfWidthSeparator,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };
        const GerhartGreekPanel = {
            label: '<i>&alpha;&beta;&eta;</i>',
            rows: [
                [
                    { latex: '\\beta', aside: 'beta' },
                    { latex: '\\delta', aside: 'delta' },
                    { latex: '\\Delta', aside: 'Delta' },
                    { latex: '\\eta', aside: 'eta' },
                    { latex: '\\alpha', aside: 'alpha' },
                    { latex: '\\pi', aside: 'pi' },
                    degreeKeycap,
                    '\\sqrt{#@}',
                    naturalLogKey,
                    logKey,
                ],
                [
                    { latex: '\\gamma', aside: 'gamma' },
                    { latex: '\\rho', aside: 'rho' },
                    { latex: '\\nabla', aside: 'nabla' },
                    { latex: '\\sigma', aside: 'sigma' },
                    { latex: '\\beta', aside: 'beta' },
                    { latex: '\\Kappa', aside: 'Kappa' },
                    { latex: '\\partial', aside: 'partial' },
                    definiteIntegral,
                    'e^{#0}',
                    separator,
                ],
                [
                    { latex: '\\nu', aside: 'nu' },
                    { latex: '\\mu', aside: 'mu' },
                    { latex: '\\omega', aside: 'omega' },
                    { latex: '\\epsilon', aside: 'epsilon' },
                    { latex: '\\tau', aside: 'tau' },
                    { latex: '\\upsilon', aside: 'upsilon' },
                    product,
                    separator,
                ],
                [
                    separator,
                    { latex: '\\mathcal D', aside: 'mathcal D' },
                    { latex: '\\mathcal L', aside: 'mathcal L' },
                    { latex: '\\mathcal R', aside: 'mathcal R' },
                    textToggleKey,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                    separator,
                ],
            ],
        };

        return [GerhartNumericPanel, GerhartGreekPanel];
    } else if (virtualKeyboardLayoutName === 'Groover') {
        const GrooverNumericPanel = {
            label: '123',
            rows: [
                [
                    { latex: '\\pi', aside: 'pi' },
                    degreeKeycap,
                    halfWidthSeparator,
                    '7',
                    '8',
                    '9',
                    '/',
                    halfWidthSeparator,
                    subscriptPlaceholder,
                    superscriptPlaceholder,
                    smallFraction,
                    separator,
                ],
                [
                    overlineKey,
                    '#@^{\\prime}',
                    halfWidthSeparator,
                    '4',
                    '5',
                    '6',
                    '*',
                    halfWidthSeparator,
                    '\\sqrt{#@}',
                    'e^{#0}',
                    naturalLogKey,
                    separator,
                ],
                ['(', ')', halfWidthSeparator, '1', '2', '3', '-', halfWidthSeparator, sinKey, cosKey, tanKey, separator],
                [
                    '\\times 10^{#0}',
                    '=',
                    halfWidthSeparator,
                    '0',
                    '.',
                    ',',
                    '+',
                    halfWidthSeparator,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };
        const GrooverCalculusPanel = {
            label: '<i>&int; dt</i>',
            rows: [
                [
                    { latex: '\\frac{d}{dt}', insert: '\\frac{d}{dt} #@' },
                    dotKey,
                    halfWidthSeparator,
                    { latex: '\\sigma', aside: 'sigma' },
                    { latex: '\\epsilon', aside: 'epsilon' },
                    { latex: '\\tau', aside: 'tau' },
                    { latex: '\\gamma', aside: 'gamma' },
                    halfWidthSeparator,
                    separator,
                    separator,
                    separator,
                    separator,
                ],
                [
                    summation,
                    definiteIntegral,
                    halfWidthSeparator,
                    { latex: '\\eta', aside: 'eta' },
                    { latex: '\\phi', aside: 'phi' },
                    { latex: '\\theta', aside: 'theta' },
                    { latex: '\\rho', aside: 'rho' },
                    halfWidthSeparator,
                    separator,
                    separator,
                    separator,
                    separator,
                ],
                [
                    '\\%',
                    '\\$',
                    halfWidthSeparator,
                    { latex: '\\alpha', aside: 'alpha' },
                    { latex: '\\beta', aside: 'beta' },
                    { latex: '\\delta', aside: 'delta' },
                    { latex: '\\mu', aside: 'mu' },
                    halfWidthSeparator,
                    separator,
                    separator,
                    separator,
                    separator,
                ],
                [
                    '\\left|#0\\right|',
                    separator,
                    halfWidthSeparator,
                    subscriptAndSuperscript,
                    subscriptPlaceholder,
                    superscriptPlaceholder,
                    smallFraction,
                    halfWidthSeparator,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };

        return [GrooverNumericPanel, GrooverCalculusPanel];
    } else if (virtualKeyboardLayoutName === 'Koretsky') {
        const KoretskyNumericPanel = {
            label: '123',
            rows: [
                [
                    '<',
                    '>',
                    halfWidthSeparator,
                    '7',
                    '8',
                    '9',
                    '/',
                    halfWidthSeparator,
                    smallFraction,
                    '\\left|#0\\right|',
                    dotKey,
                    separator,
                ],
                [
                    '(',
                    ')',
                    halfWidthSeparator,
                    '4',
                    '5',
                    '6',
                    '*',
                    halfWidthSeparator,
                    subscriptPlaceholder,
                    superscriptPlaceholder,
                    subscriptAndSuperscript,
                    separator,
                ],
                [
                    '\\approx',
                    centerDot,
                    halfWidthSeparator,
                    '1',
                    '2',
                    '3',
                    '-',
                    halfWidthSeparator,
                    overlineKey,
                    { latex: '\\overrightarrow{\\scriptstyle{#0}}', insert: '\\overrightarrow{#0}' },
                    { latex: '\\hat{\\scriptstyle{#0}}', insert: '\\hat{#0}' },
                    separator,
                ],
                [
                    '\\ne',
                    '=',
                    halfWidthSeparator,
                    '0',
                    '.',
                    ',',
                    '+',
                    halfWidthSeparator,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };
        const KoretskyGreekPanel = {
            label: '<i>&alpha;&beta;&eta;</i>',
            rows: [
                [
                    { latex: '\\alpha', aside: 'alpha' },
                    { latex: '\\gamma', aside: 'gamma' },
                    { latex: '\\Delta', aside: 'Delta' },
                    { latex: '\\kappa', aside: 'kappa' },
                    separator,
                    separator,
                    halfWidthSeparator,
                    '\\pi',
                    degreeKeycap,
                    '\\sqrt{#@}',
                    naturalLogKey,
                    logKey,
                    separator,
                ],
                [
                    { latex: '\\phi', aside: 'phi' },
                    { latex: '\\omega', aside: 'omega' },
                    { latex: '\\xi', aside: 'xi' },
                    halfWidthSeparator,
                    separator,
                    '\\infty',
                    '\\partial',
                    summation,
                    definiteIntegral,
                    indefiniteIntegral,
                    expKey,
                ],
                [
                    separator,
                    separator,
                    separator,
                    separator,
                    separator,
                    halfWidthSeparator,
                    textToggleKey,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };

        return [KoretskyNumericPanel, KoretskyGreekPanel];
    } else if (virtualKeyboardLayoutName === 'Liberatore') {
        const LiberatoreNumericPanel = {
            label: '123',
            rows: [
                ['<', '>', halfWidthSeparator, '7', '8', '9', '/', halfWidthSeparator, smallFraction, dotKey, separator, separator],
                [
                    '(',
                    ')',
                    halfWidthSeparator,
                    '4',
                    '5',
                    '6',
                    '*',
                    halfWidthSeparator,
                    subscriptPlaceholder,
                    superscriptPlaceholder,
                    subscriptAndSuperscript,
                    separator,
                ],
                [
                    '\\approx',
                    centerDot,
                    halfWidthSeparator,
                    '1',
                    '2',
                    '3',
                    '-',
                    halfWidthSeparator,
                    overlineKey,
                    { latex: '\\overrightarrow{\\scriptstyle{#0}}', insert: '\\overrightarrow{#0}' },
                    separator,
                    separator,
                ],
                [
                    '\\ne',
                    '=',
                    halfWidthSeparator,
                    '0',
                    '.',
                    negate,
                    '+',
                    halfWidthSeparator,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };
        const LiberatoreGreekPanel = {
            label: '<i>&alpha;&beta;&eta;</i>',
            rows: [
                [
                    { latex: '\\Delta', aside: 'Delta' },
                    { latex: '\\rho', aside: 'rho' },
                    { latex: '\\xi', aside: 'xi' },
                    separator,
                    degreeKeycap,
                    '\\sqrt{#@}',
                    naturalLogKey,
                    { latex: '\\log_{10}', insert: '\\log_{10} #0' },
                ],
                [
                    { latex: '\\nu', aside: 'nu' },
                    { latex: '\\mu', aside: 'mu' },
                    { latex: '\\pi', aside: 'pi' },
                    separator,
                    summation,
                    definiteIntegral,
                    indefiniteIntegral,
                    expKey,
                ],
                [separator, separator, separator, separator, '[left]', '[right]', backspaceWidthOne, nextStepKeyCap],
            ],
        };

        return [LiberatoreNumericPanel, LiberatoreGreekPanel];
    } else if (virtualKeyboardLayoutName === 'LSAS') {
        const LSASNumericPanel = {
            label: '123',
            rows: [
                [
                    separator,
                    halfWidthSeparator,
                    { latex: '\\pi', aside: 'pi' },
                    'j',
                    halfWidthSeparator,
                    '7',
                    '8',
                    '9',
                    '/',
                    halfWidthSeparator,
                    subscriptPlaceholder,
                    superscriptPlaceholder,
                    smallFraction,
                    separator,
                ],
                [
                    separator,
                    halfWidthSeparator,
                    degreeKeycap,
                    separator,
                    halfWidthSeparator,
                    '4',
                    '5',
                    '6',
                    '*',
                    halfWidthSeparator,
                    squareRootKeycapPlain,
                    { latex: 'e^{#0}', shift: naturalLogKey },
                    { latex: '10^x', insert: '10^{#0}', shift: logKey },
                    separator,
                ],
                [
                    separator,
                    halfWidthSeparator,
                    { latex: '\\angle', aside: 'angle' },
                    { latex: '\\left|#0\\right|', aside: 'magnitude' },
                    halfWidthSeparator,
                    '1',
                    '2',
                    '3',
                    '-',
                    halfWidthSeparator,
                    sinWithShiftInverse,
                    cosWithShiftInverse,
                    tanWithShiftInverse,
                    separator,
                ],
                [
                    '[shift]',
                    '\\times 10^{#0}',
                    '=',
                    halfWidthSeparator,
                    '0',
                    '.',
                    ',',
                    '+',
                    halfWidthSeparator,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };
        const LSASCalculusPanel = {
            label: '<i>&int; dt</i>',
            rows: [
                [
                    { latex: '\\frac{d^n}{dt^n}', insert: '\\frac{d^{#0}}{dt^{#0}}#0' },
                    definiteIntegral,
                    halfWidthSeparator,
                    '\\mathbf{x}',
                    '\\mathbf{y}',
                    { latex: '\\mathbf{M}^{-1}', insert: '(#0)^{-1}', aside: 'inverse' },
                    '\\mathbf{\\Phi}',
                    halfWidthSeparator,
                    { latex: '\\theta', aside: 'theta' },
                    { latex: '\\phi', aside: 'phi' },
                    { latex: '\\gamma', aside: 'gamma' },
                    separator,
                ],
                [
                    summation,
                    product,
                    halfWidthSeparator,
                    '\\mathbf{A}',
                    '\\mathbf{B}',
                    '\\mathbf{C}',
                    '\\mathbf{D}',
                    halfWidthSeparator,
                    { latex: '\\omega', aside: 'omega' },
                    { latex: '\\zeta', aside: 'zeta' },
                    { latex: '\\lambda', aside: 'lambda' },
                    separator,
                ],
                [
                    {
                        latex: '\\mathscr{L}',
                        insert: '\\mathscr{L}{#0}',
                        shift: { latex: '\\mathscr{L}^{-1}', insert: '\\mathscr{L}^{-1}{#0}' },
                    },
                    {
                        latex: '\\mathcal{Z}',
                        insert: '\\mathcal{Z}{#0}',
                        shift: { latex: '\\mathcal{Z}^{-1}', insert: '\\mathcal{Z}^{-1}{#0}' },
                    },
                    halfWidthSeparator,
                    { latex: '\\mathbf{M}', aside: 'create matrix', insert: '\\begin{pmatrix}#0 & #0 \\\\ #0 & #0 \\end{pmatrix}' },
                    '\\rightarrow',
                    '\\infty',
                    '\\%',
                    halfWidthSeparator,
                    { latex: '\\alpha', aside: 'alpha' },
                    { latex: '\\beta', aside: 'beta' },
                    { latex: '\\Omega', aside: 'Omega' },
                    separator,
                ],
                [
                    '[shift]',
                    separator,
                    { latex: '\\delta', aside: 'delta' },
                    subscriptPlaceholder,
                    superscriptPlaceholder,
                    smallFraction,
                    halfWidthSeparator,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };

        return [LSASNumericPanel, LSASCalculusPanel];
    } else if (virtualKeyboardLayoutName === 'Montgomery') {
        const MontgomeryFunctionsPanel = {
            label: 'Functions',
            rows: [
                [
                    separator,
                    halfWidthSeparator,
                    '7',
                    '8',
                    '9',
                    '/',
                    halfWidthSeparator,
                    { latex: '#@^{\\prime}', shift: { latex: '\\hat{\\scriptstyle{#0}}', insert: '\\hat{#0}' } },
                    subscriptAndSuperscript,
                    { latex: '|', shift: '\\left|#0\\right|' },
                    { latex: '\\lt', shift: '\\leq' },
                    { latex: '\\gt', shift: '\\geq' },
                    { latex: '!', shift: '\\%' },
                ],
                [
                    separator,
                    halfWidthSeparator,
                    '4',
                    '5',
                    '6',
                    '*',
                    halfWidthSeparator,
                    { latex: '#@_{#?}', shift: superscriptPlaceholder },
                    centerDot,
                    { class: 'small', latex: '\\sqrt{#0}', shift: { class: 'small', latex: '\\sqrt[#?]{{#0}}' } },
                    { latex: '(', shift: '[' },
                    { latex: ')', shift: ']' },
                    summation,
                ],
                [
                    separator,
                    halfWidthSeparator,
                    '1',
                    '2',
                    '3',
                    '-',
                    halfWidthSeparator,
                    {
                        latex: '\\bar{\\scriptstyle{#0}}',
                        insert: '\\bar{#0}',
                        shift: { latex: '\\bar{\\bar{\\scriptstyle{#0}}}', insert: '\\bar{\\bar{#0}}' },
                    },
                    { latex: '\\ln', insert: '\\ln #0', shift: { latex: 'e^{#0}' } },
                    { latex: '\\log', insert: '\\log #0', shift: { latex: '\\times 10^{#0}' } },
                    { latex: '\\{', shift: '\\}' },
                    definiteIntegral,
                    { latex: 'd', shift: '\\partial' },
                ],
                [
                    '[shift]',
                    '0',
                    '.',
                    ',',
                    '+',
                    halfWidthSeparator,
                    separator,
                    { latex: '\\pm', shift: '\\infty' },
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };
        const MontgomerySymbolsPanel = {
            label: 'Symbols',
            rows: [
                [
                    separator,
                    halfWidthSeparator,
                    { latex: '\\alpha', aside: 'alpha', shift: 'A' },
                    { latex: '\\beta', aside: 'beta', shift: 'B' },
                    { latex: '\\gamma', aside: 'gamma', shift: '\\Gamma' },
                    { latex: '\\delta', aside: 'delta', shift: '\\Delta' },
                    separator,
                    halfWidthSeparator,
                    { class: 'small', latex: '\\left(\\begin{matrix} #0 \\\\ #0 \\end{matrix}\\right)' },
                    '\\cup',
                    '\\cap',
                ],
                [
                    separator,
                    halfWidthSeparator,
                    { latex: '\\epsilon', aside: 'epsilon', shift: 'E' },
                    { latex: '\\eta', aside: 'eta', shift: 'H' },
                    { latex: '\\theta', aside: 'theta', shift: '\\Theta' },
                    { latex: '\\lambda', aside: 'lambda', shift: '\\Lambda' },
                    separator,
                    halfWidthSeparator,
                    '\\notin',
                    { latex: 'P_{r}^{n}', insert: 'P{#0}^{#0}', aside: '???' },
                    { latex: 'C_r^n', insert: 'C_{#0}^{#0}', aside: '???' },
                ],
                [
                    separator,
                    halfWidthSeparator,
                    { latex: '\\mu', aside: 'mu', shift: 'M' },
                    { latex: '\\nu', aside: 'nu', shift: 'N' },
                    { latex: '\\tau', aside: 'tau', shift: 'T' },
                    { latex: '\\pi', aside: 'pi', shift: '\\Pi' },
                    separator,
                    halfWidthSeparator,
                    { latex: '^\\varnothing', aside: 'null set' },
                    '\\in',
                    '\\ni',
                ],
                [
                    separator,
                    '[shift]',
                    { latex: '\\rho', aside: 'rho', shift: 'P' },
                    { latex: '\\sigma', aside: 'sigma', shift: summation },
                    { latex: '\\phi', aside: 'phi', shift: '\\Phi' },
                    { latex: '\\chi', aside: 'chi', shift: 'X' },
                    { latex: '\\varepsilon', aside: 'epsilon (alt.)' },
                    halfWidthSeparator,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };

        return [MontgomeryFunctionsPanel, MontgomerySymbolsPanel];
    } else if (virtualKeyboardLayoutName === 'Moran') {
        const MoranNumericPanel = {
            label: '123',
            rows: [
                [
                    '<',
                    '>',
                    halfWidthSeparator,
                    '7',
                    '8',
                    '9',
                    '/',
                    halfWidthSeparator,
                    smallFraction,
                    absoluteSmallFraction,
                    separator,
                    separator,
                ],
                [
                    '(',
                    ')',
                    halfWidthSeparator,
                    '4',
                    '5',
                    '6',
                    '*',
                    halfWidthSeparator,
                    subscriptPlaceholder,
                    superscriptPlaceholder,
                    separator,
                    separator,
                ],
                [
                    '\\approx',
                    centerDot,
                    halfWidthSeparator,
                    '1',
                    '2',
                    '3',
                    '-',
                    halfWidthSeparator,
                    barKey,
                    dotKey,
                    scientificNotationWithPlaceholder,
                    textToggleKey,
                ],
                [
                    '\\ne',
                    '=',
                    halfWidthSeparator,
                    '0',
                    '.',
                    ',',
                    '+',
                    halfWidthSeparator,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };
        const MoranGreekPanel = {
            label: '<i>&alpha;&beta;&eta;</i>',
            rows: [
                [
                    { latex: '\\beta', aside: 'beta' },
                    { latex: '\\delta', aside: 'delta' },
                    { latex: '\\Delta', aside: 'Delta' },
                    { latex: '\\eta', aside: 'eta' },
                    separator,
                    '\\pi',
                    degreeKeycap,
                    '\\sqrt{#@}',
                    naturalLogKey,
                    logKey,
                ],
                [
                    { latex: '\\gamma', aside: 'gamma' },
                    { latex: '\\rho', aside: 'rho' },
                    { latex: '\\sigma', aside: 'sigma' },
                    { latex: '\\tau', aside: 'tau' },
                    separator,
                    summation,
                    '\\$',
                    definiteIntegral,
                    indefiniteIntegral,
                    'e^{#0}',
                ],
                [
                    { latex: '\\omega', aside: 'omega' },
                    separator,
                    separator,
                    separator,
                    separator,
                    textToggleKey,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };

        return [MoranNumericPanel, MoranGreekPanel];
    } else if (virtualKeyboardLayoutName === 'Nise') {
        const NiseNumericPanel = {
            label: '123',
            rows: [
                [
                    separator,
                    halfWidthSeparator,
                    { latex: '\\pi', aside: 'pi' },
                    'j',
                    halfWidthSeparator,
                    '7',
                    '8',
                    '9',
                    '/',
                    halfWidthSeparator,
                    subscriptPlaceholder,
                    superscriptPlaceholder,
                    subscriptAndSuperscript,
                    smallFraction,
                ],
                [
                    separator,
                    halfWidthSeparator,
                    { latex: '\\angle', aside: 'angle' },
                    { latex: '\\left|#0\\right|', aside: 'magnitude' },
                    halfWidthSeparator,
                    '4',
                    '5',
                    '6',
                    '*',
                    halfWidthSeparator,
                    'e^{#0}',
                    naturalLogKey,
                    { latex: '10^x', insert: '10^{#0}' },
                    logKey,
                ],
                [
                    separator,
                    halfWidthSeparator,
                    '(',
                    ')',
                    halfWidthSeparator,
                    '1',
                    '2',
                    '3',
                    '-',
                    halfWidthSeparator,
                    '\\sqrt{#0}',
                    sinWithShiftInverse,
                    cosWithShiftInverse,
                    tanWithShiftInverse,
                ],
                [
                    '[shift]',
                    '\\times 10^{#0}',
                    '=',
                    halfWidthSeparator,
                    '0',
                    '.',
                    ',',
                    '+',
                    halfWidthSeparator,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };
        const NiseCalculusPanel = {
            label: '<i>&int; dt</i>',
            rows: [
                [
                    separator,
                    halfWidthSeparator,
                    { latex: '\\frac{d^{#0}}{dt^{#0}}', insert: '\\frac{d^{#0}}{dt^{#0}}{#0}' },
                    definiteIntegral,
                    halfWidthSeparator,
                    { latex: '\\mathbf{M}', aside: 'create matrix', insert: '\\begin{pmatrix}#0 & #0 \\\\ #0 & #0 \\end{pmatrix}' },
                    { latex: '\\mathrm{det}\\,\\mathbf{M}', insert: '\\mathrm{det}(#0)', aside: 'determinate' },
                    { latex: '\\mathbf{M}^{-1}', insert: '{#@}^{-1}', aside: 'inverse' },
                    separator,
                    halfWidthSeparator,
                    { latex: '\\theta', aside: 'theta' },
                    { latex: '\\phi', aside: 'phi' },
                    { latex: '\\gamma', aside: 'gamma' },
                    separator,
                ],
                [
                    separator,
                    halfWidthSeparator,
                    summation,
                    product,
                    halfWidthSeparator,
                    '\\mathbf{A}',
                    '\\mathbf{B}',
                    '\\mathbf{C}',
                    '\\mathbf{D}',
                    halfWidthSeparator,
                    { latex: '\\omega', aside: 'omega' },
                    { latex: '\\zeta', aside: 'zeta' },
                    { latex: '\\lambda', aside: 'lambda' },
                    separator,
                ],
                [
                    separator,
                    halfWidthSeparator,
                    {
                        latex: '\\mathscr{L}',
                        insert: '\\mathscr{L}{#0}',
                        shift: { latex: '\\mathscr{L}^{-1}', insert: '\\mathscr{L}^{-1}{#0}' },
                    },
                    {
                        latex: '\\mathcal{Z}',
                        insert: '\\mathcal{Z}{#0}',
                        shift: { latex: '\\mathcal{Z}^{-1}', insert: '\\mathcal{Z}^{-1}{#0}' },
                    },
                    halfWidthSeparator,
                    '\\mathbf{\\Phi}',
                    '\\mathbf{I}',
                    '\\mathbf{x}',
                    '\\mathbf{y}',
                    halfWidthSeparator,
                    degreeKeycap,
                    { latex: '\\alpha', aside: 'alpha' },
                    { latex: '\\beta', aside: 'beta' },
                    separator,
                ],
                [
                    '[shift]',
                    { latex: '\\lim_{#0}', insert: '\\lim_{#0}{#?}' },
                    '\\rightarrow',
                    halfWidthSeparator,
                    '\\infty',
                    { latex: '\\delta', aside: 'delta' },
                    { latex: '\\Delta', aside: 'Delta' },
                    '\\%',
                    halfWidthSeparator,
                    '[left]',
                    '[right]',
                    backspaceWidthOne,
                    nextStepKeyCap,
                ],
            ],
        };

        return [NiseNumericPanel, NiseCalculusPanel];
    }

    // No matching layout found
    return null;
}
